import { FC, useEffect, useState } from "react";

import {
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHFWithLabel,
	LabelPosition,
	RHookFormNumberFieldWithLabel,
	RHookFormTextFieldWithLabel,
} from "@websential/cosmic-v20";
import { Box, Grid, Typography } from "@mui/material";
import {
	formatAmountCommaSeperator,
	formatAmountToFloat,
} from "../../../common/Common";

interface IBcEhtInfoSetupFieldsPanel {
	onUpdateClickedHandler: (
		exemptionAmount: string | number,
		notchAmount: string | number
	) => void;
	bcEhtSettingDataByYear: any;
	formMethods: any;
	bcEhtData: any;
}
export const BcEhtInfoSetupFieldsPanel: FC<IBcEhtInfoSetupFieldsPanel> = ({
	onUpdateClickedHandler,
	bcEhtSettingDataByYear,
	formMethods,
	bcEhtData,
}) => {
	const [proRateClicked, setProRateClicked] = useState(false);
	const [calculatedExemptionAmount, setCalculatedExemptionAmount] =
		useState("0.00");
	const [calculatedNotchAmount, setCalculatedNotchAmount] = useState("0.00");
	const [numberOfDays, setNumberOfDays] = useState(0);
	const [exemptionAmount, setExemptionAmount] = useState(0);
	const [notchAmount, setNotchAmount] = useState(0);
	useEffect(() => {
		if (bcEhtSettingDataByYear) {
			setExemptionAmount(bcEhtSettingDataByYear.exemptionAmount);
			setNotchAmount(bcEhtSettingDataByYear.notchAmount);
			formMethods.setValue(
				"exemptionAmount",
				bcEhtSettingDataByYear.exemptionAmount
			);
			formMethods.setValue("notchAmount", bcEhtSettingDataByYear.notchAmount);
		}
	}, [bcEhtSettingDataByYear]);
	const onCharitableOrgClicked = (event: any) => {
		let value = event.value;
		formMethods.setValue("days", 0);
		setCalculatedExemptionAmount("0.00");
		setCalculatedNotchAmount("0.00");
		if (value) {
			setExemptionAmount(bcEhtSettingDataByYear.charitableExcemptionAmount);
			setNotchAmount(bcEhtSettingDataByYear.charitableNotchAmount);
			formMethods.setValue(
				"exemptionAmount",
				bcEhtSettingDataByYear.charitableExcemptionAmount
			);
			formMethods.setValue(
				"notchAmount",
				bcEhtSettingDataByYear.charitableNotchAmount
			);
		} else {
			setExemptionAmount(bcEhtSettingDataByYear.exemptionAmount);
			setNotchAmount(bcEhtSettingDataByYear.notchAmount);
			formMethods.setValue(
				"exemptionAmount",
				bcEhtSettingDataByYear.exemptionAmount
			);
			formMethods.setValue("notchAmount", bcEhtSettingDataByYear.notchAmount);
		}
	};
	const onProRateClickedHandler = () => {
		setProRateClicked((prev: any) => !prev);
	};
	let TotalDaysInOneYear = 365;
	const onValueChangeHandler = (e: any) => {
		let value = e.target.value;
		setNumberOfDays(Number(value));
		let calculateExemptionAmount =
			(Number(value) / TotalDaysInOneYear) * Number(exemptionAmount);
		setCalculatedExemptionAmount(
			formatAmountCommaSeperator(calculateExemptionAmount)
		);

		let calculateNotchAmount =
			(Number(value) / TotalDaysInOneYear) * Number(notchAmount);
		setCalculatedNotchAmount(formatAmountCommaSeperator(calculateNotchAmount));
	};
	return (
		<Box>
			<RHookFormNumberFieldWithLabel
				label="Previous Year's BC Remuneration"
				id="previousYearBc"
				testId={`previousYearBc`}
			/>
			<RHookFormNumberFieldWithLabel
				label="Current Year's Expected BC Remuneration"
				id="currentYearExpectedBc"
				testId={`currentYearExpectedBc`}
			/>
			<EnhancedDropDownRHFWithLabel
				label="Charitable Org."
				selectName="charitableOrg"
				testId={`charitableOrg`}
				items={[
					{ label: "No", value: false },
					{ label: "Yes", value: true },
				]}
				selectedItem={false}
				onSelectionChangeHandler={onCharitableOrgClicked}
			/>
			<Grid
				size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
				sx={{ display: "flex" }}
			>
				<Grid size={{ xs: 12, sm: 12, md: 9, lg: 9, xl: 9 }}>
					<RHookFormNumberFieldWithLabel
						label="Exemption Amount"
						id="exemptionAmount"
						testId={`exemptionAmount`}
					/>
				</Grid>
				<Grid
					size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
					sx={{ textAlign: "right" }}
				>
					<Typography
						sx={{
							color: "#2baaed",
							marginTop: "30px",
							cursor: "pointer",
						}}
						onClick={onProRateClickedHandler}
					>
						ProRate [?]
					</Typography>
				</Grid>
			</Grid>
			<RHookFormNumberFieldWithLabel
				label="Notch Amount"
				id="notchAmount"
				testId={`notchAmount`}
			/>
			{proRateClicked && (
				<Box
					sx={{
						textAlign: "right",
						color: "rgba(0, 0, 0, 0.6)",
						fontSize: "0.875rem",
					}}
				>
					<Typography
						sx={{
							fontSize: "0.875rem",
							textAlign: "left",
							paddingBottom: "10px",
						}}
					>
						If you are part year employer i.e you have started business in
						middle of the year, you can prorate exemption and notch amounts.
					</Typography>
					<RHookFormTextFieldWithLabel
						label="Permanent Est in BC for No. of days in year"
						id="days"
						testId={`days`}
						defaultValue={0}
						onChangeHandler={onValueChangeHandler}
						labelColumns={8}
						fieldColumns={4}
						labelPosition={LabelPosition.Left}
					/>
					<Typography>
						Exemption amount = {numberOfDays}/{TotalDaysInOneYear} * $
						{formatAmountCommaSeperator(exemptionAmount)} = $
						{calculatedExemptionAmount}
					</Typography>
					<Typography>
						Notch amount = {numberOfDays}/{TotalDaysInOneYear} * $
						{formatAmountCommaSeperator(notchAmount)} = ${calculatedNotchAmount}
					</Typography>

					<EnhancedButtonPrimeReact
						label="Update"
						raised
						severity={EnhancedButtonPrimeReactSeverity.Success}
						onClickHandler={() =>
							onUpdateClickedHandler(
								formatAmountToFloat(calculatedExemptionAmount),
								formatAmountToFloat(calculatedNotchAmount)
							)
						}
						style={{ margin: "20px 0 30px 0" }}
						disabled={bcEhtData.remitted ? true : false}
					/>
				</Box>
			)}
		</Box>
	);
};
